import { useMemo } from 'preact/hooks';
import type { Ref, RefCallback } from 'preact';

// Accepts preact Refs plus useRef<T>() objects whose current is T | undefined
type MergeableRef<T> = Ref<T> | { current: T | null | undefined } | null | undefined;

// Assign a value to a ref of either shape (callback or object).
export function applyRef<T>(ref: MergeableRef<T>, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    (ref as { current: T | null }).current = value;
  }
}

/*
  Merge an internal ref with a consumer-forwarded one so both receive the
  element. Needed because React 19 (and wrappers like emotion `styled()`)
  deliver `ref` as a regular prop: components must destructure it out of
  their rest props — otherwise a later `{...props}` spread clobbers the
  internal ref — and re-attach it alongside their own.
*/
export function useMergedRef<T>(...refs: Array<MergeableRef<T>>): RefCallback<T> {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- variadic refs are the dependencies
  return useMemo(() => (value: T | null) => refs.forEach((ref) => applyRef(ref, value)), refs);
}
